import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PayPalEcStatesEnum } from 'i2g-ng-payment';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { Map } from 'immutable';
import { Account } from '../../../account-data/account.model';
import { IDocumentDataDispatcher } from '../../../document-data/document-data-dispatcher.interface';
import { DocumentTypesEnum } from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { DocumentPayments } from '../../../document-data/sub-models/document-payments.model';
import { DocumentPreview } from '../../../document-data/sub-models/document-preview.model';
import { Schedule } from '../../../document-data/sub-models/schedule.model';
import { States } from '../../../document-data/sub-models/states.model';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { DepositToggleValue } from '../../../settings-data/sub-models/company-settings/deposit-toggle-value.model';
import { PaymentReminders } from '../../../settings-data/sub-models/company-settings/payment-reminders.model';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { TrackingAdditionalPropertiesBuilder } from '../../../tracking/tracking-additional-properties.builder';
import {
  IPaymentOptionsAdditionalProperties,
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';

@Component({
  selector: 'app-aside-actions',
  templateUrl: './aside-actions.component.html',
  styleUrls: ['./aside-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AsideActionsComponent implements OnInit, OnChanges {
  @Input() documentDataDispatcher: IDocumentDataDispatcher;
  @Input() documentType: DocumentTypesEnum;
  @Input() documentId: string;
  @Input() documentNumber: string;
  @Input() documentDueDate: string;
  @Input() documentFeatureName: string;
  @Input() documentState: States;
  @Input() documentCalculationResult: CalculationResult;
  @Input() documentDate: string;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() documentPayments: DocumentPayments;
  @Input() documentBilling: Billing;
  @Input() documentPaymentUrl: string;
  @Input() documentSchedule: Schedule;
  @Input() documentRevisionId: string;
  @Input() sourceDocumentPreview: DocumentPreview;
  @Input() linkedDocumentPreview: DocumentPreview;
  @Input() useOutstandingBalance: boolean;
  @Input() disableSendButton: boolean;
  @Input() showSendButton: boolean;
  @Input() showPaid: boolean;
  @Input() showTotal: boolean;
  @Input() showPaymentActions: boolean;
  @Input() showI2gMoney: boolean;
  @Input() showBankTransfer: boolean;
  @Input() showCardPayments: boolean;
  @Input() showPaypalEc: boolean;
  @Input() paypalEcStatus: PayPalEcStatesEnum;
  @Input() i2gMoneyEnabled: boolean;
  @Input() bankTransferEnabled: boolean;
  @Input() cardPaymentsEnabled: boolean;
  @Input() paypalEcEnabled: boolean;
  @Input() enableSchedules: boolean;
  @Input() enableDeposits: boolean;
  @Input() underwritingUrl: string;
  @Input() companySettings: CompanySettings;
  @Input() paymentsSettings: PaymentsSettings;
  @Input() remindersSettings: PaymentReminders;
  @Input() documentTrackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() featureToggles: Toggles;
  @Input() showPaymentReminders: boolean;
  @Input() account: Account;
  @Input() deposit: Deposit;
  @Input() addDepositForFuture: DepositToggleValue;
  @Input() document: Document;
  @Input() documentHasUnsavedChanges: boolean;
  @Input() depositRequestFeatureName: string;

  @Output() sendDocument: EventEmitter<void> = new EventEmitter<void>();
  @Output() updateRecurringSchedule: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();
  @Output() growthCta: EventEmitter<void> = new EventEmitter<void>();
  @Output() updateDocumentContent: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();
  @Output() updateSettingsContent: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();

  linkedLabelKey: string;
  sourceLabelKey: string;
  enableRecurring: boolean;
  DocumentTypesEnum: typeof DocumentTypesEnum;
  buttonSendTapped: ITrackingAction;
  paymentOptionsTrackingProperties: IPaymentOptionsAdditionalProperties;

  constructor(
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingAdditionalPropertiesBuilder: TrackingAdditionalPropertiesBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.documentSettings ||
        changes.paymentsSettings ||
        changes.documentRevisionId) &&
      this.paymentsSettings &&
      this.documentSettings &&
      this.documentRevisionId
    ) {
      this.paymentOptionsTrackingProperties = this._trackingAdditionalPropertiesBuilder.buildPaymentOptions(
        this.paymentsSettings,
        this.documentSettings,
        this.documentRevisionId,
        this.showI2gMoney
      );
      // Track when Send CTA is tapped with file attachment experiment
      if (this.featureToggles.isAllAccess(Toggles.KEYS.FILE_ATTACHMENT)) {
        this.paymentOptionsTrackingProperties['variant_key'] =
          'file_attachments';
      }
    }

    if (changes.featureToggles && this.featureToggles) {
      this.enableRecurring = this.featureToggles.isAllAccess(
        Toggles.KEYS.SCHEDULE
      );
    }

    if (changes.linkedDocumentPreview && this.linkedDocumentPreview) {
      const isConvertedTo =
        this.document.documentType === DocumentTypesEnum.estimate &&
        this.linkedDocumentPreview.documentType === DocumentTypesEnum.invoice;

      this.linkedLabelKey = isConvertedTo ? 'CONVERTED_TO' : 'COPIED_TO';
    }

    if (changes.sourceDocumentPreview && this.sourceDocumentPreview) {
      const isConvertedFrom =
        this.document.documentType === DocumentTypesEnum.invoice &&
        this.sourceDocumentPreview.documentType === DocumentTypesEnum.estimate;

      this.sourceLabelKey = isConvertedFrom ? 'CONVERTED_FROM' : 'COPIED_FROM';
    }
  }

  ngOnInit() {
    this.DocumentTypesEnum = DocumentTypesEnum;
    this.buttonSendTapped = this._trackingActionBuilder.buildButtonTapped(
      'email',
      this.trackingScreenName
    );
  }
}
